import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Input } from '../ui/input';
import { Card, CardContent, CardFooter } from "../ui/card";
import { Button } from '../ui/button';
import { authAPI } from '../../services/api';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Redirect if token is missing
  useEffect(() => {
    if (!token) {
      navigate(-1); // Go back
    }
  }, [token, navigate]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'Le mot de passe doit comporter au moins 8 caractères.';
    if (!/[A-Z]/.test(pwd)) return 'Le mot de passe doit contenir une lettre majuscule.';
    if (!/[0-9]/.test(pwd)) return 'Le mot de passe doit contenir un chiffre.';
    return null;
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validatePassword(password);
    if (validationError) return setError(validationError);

    if (password !== confirmPassword) {
      return setError('Les mots de passe ne correspondent pas.');
    }

    try {
      await authAPI.resetPassword({
        token: token || '',
        newPassword: password,
      });
      setSuccess(true);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Une erreur est survenue.';
      setError(msg);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-accent py-12 px-4 sm:px-6 lg:px-8 dark:bg-gray-900">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center justify-center text-center">
          <h1 className="mt-6 text-3xl font-serif font-bold tracking-tight">Réinitialiser le mot de passe</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Entrez un nouveau mot de passe sécurisé pour accéder à votre compte.
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            {success ? (
              <p className="text-green-600 text-center">
                Mot de passe mis à jour. Vous pouvez maintenant vous connecter.
              </p>
            ) : (
              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <Input
                    type="password"
                    placeholder="Nouveau mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Input
                    type="password"
                    placeholder="Confirmer le mot de passe"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
                <Button type="submit" className="mt-4 w-full">Réinitialiser</Button>
              </form>
            )}
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <p className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
              Retour à{" "}
              <a href="/signin" className="font-medium text-elille-blue hover:underline">
                la connexion
              </a>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
